import { Box, Card, Divider, Grid, Typography, useTheme } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import EngineeringIcon from '@mui/icons-material/Engineering';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import HistoryIcon from '@mui/icons-material/History';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BusinessIcon from '@mui/icons-material/Business';
import LabelImportantIcon from '@mui/icons-material/LabelImportant';
import NotesIcon from '@mui/icons-material/Notes';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import FechaFunction from '../../../function/FechaFunction';

const ESTADO_COLORS = {
    dark: {
        Cerrada:    { bg: '#1b3a2d', border: '#2e7d32', text: '#81c784' },
        Asignada:   { bg: '#1a2d4a', border: '#1976d2', text: '#64b5f6' },
        Despachada: { bg: '#2d2347', border: '#7e57c2', text: '#b39ddb' },
        Almacenada: { bg: '#3a3015', border: '#ffa000', text: '#ffd54f' },
        'Sin Asignar': { bg: '#3a2020', border: '#c62828', text: '#ef9a9a' },
        default:    { bg: '#3a2c14', border: '#bf360c', text: '#ffb74d' },
    },
    light: {
        Cerrada:    { bg: '#e8f5e9', border: '#a5d6a7', text: '#2e7d32' },
        Asignada:   { bg: '#e3f2fd', border: '#90caf9', text: '#1565c0' },
        Despachada: { bg: '#ede7f6', border: '#b39ddb', text: '#5e35b1' },
        Almacenada: { bg: '#fff8e1', border: '#ffe082', text: '#f57c00' },
        'Sin Asignar': { bg: '#ffebee', border: '#ef9a9a', text: '#c62828' },
        default:    { bg: '#fff3e0', border: '#ffcc80', text: '#e65100' },
    },
};

const Row = ({ icon, label, value: val, isDarkMode }) => val ? (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
        {icon}
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: isDarkMode ? '#78909c' : '#90a4ae', whiteSpace: 'nowrap', flexShrink: 0 }}>{label}</Typography>
        <Typography sx={{ fontSize: 11.5, color: isDarkMode ? '#cfd8dc' : '#37474f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</Typography>
    </Box>
) : null;

const OTCard = ({ ot, isDarkMode }) => {
    const estado = ot.estado_ot || '';
    const palette = isDarkMode ? ESTADO_COLORS.dark : ESTADO_COLORS.light;
    const estadoColors = palette[estado] || palette.default;
    const ultimoHistorial = Array.isArray(ot.history_ot) && ot.history_ot.length > 0
        ? ot.history_ot[ot.history_ot.length - 1]
        : null;
    const iconSx = { fontSize: 11, color: isDarkMode ? '#78909c' : '#90a4ae', flexShrink: 0 };

    return (
        <Card sx={{
            background: isDarkMode ? '#1a2332' : '#fff',
            border: isDarkMode ? `1px solid ${estadoColors.border}` : `1px solid #dce6f5`,
            borderLeft: `3px solid ${estadoColors.text}`,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 4px rgba(21,41,75,0.07)',
        }}>
            {/* HEADER */}
            <Box sx={{
                px: 1.5, py: 1,
                background: isDarkMode ? '#15202b' : '#f1f5fb',
                borderBottom: isDarkMode ? '1px solid #2d3a4a' : '1px solid #e3eaf2',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.8
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
                    <AssignmentIndIcon sx={{ fontSize: 15, color: estadoColors.text, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: isDarkMode ? '#e0e7f0' : '#1a2332' }}>
                        #{ot.nroOTText_ot}
                    </Typography>
                    {ot.tipoOT && (
                        <Typography sx={{ fontSize: 10.5, color: isDarkMode ? '#90a4ae' : '#78909c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            · {ot.tipoOT}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {ot.prioridad_ot && ot.prioridad_ot !== 'NORMAL' && (
                        <Box sx={{ px: 0.8, py: 0.15, borderRadius: '6px', background: ot.prioridad_ot === 'ALTA' ? (isDarkMode ? '#3a2020' : '#ffebee') : (isDarkMode ? '#1b3a2d' : '#e8f5e9') }}>
                            <Typography sx={{ fontSize: 9, fontWeight: 800, color: ot.prioridad_ot === 'ALTA' ? (isDarkMode ? '#ef9a9a' : '#c62828') : (isDarkMode ? '#81c784' : '#2e7d32') }}>
                                {ot.prioridad_ot}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ px: 0.9, py: 0.2, borderRadius: '8px', background: estadoColors.bg, border: `1px solid ${estadoColors.border}` }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: estadoColors.text }}>{estado}</Typography>
                    </Box>
                </Box>
            </Box>

            {/* BODY */}
            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                {ot.datosCliente_ot?.clienteNombre_ot && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 12, color: isDarkMode ? '#78909c' : '#90a4ae', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDarkMode ? '#e0e7f0' : '#263238' }}>
                            {ot.datosCliente_ot.clienteNombre_ot}
                        </Typography>
                        {ot.datosCliente_ot.clienteTelefono_ot && ot.datosCliente_ot.clienteTelefono_ot !== '-' && (
                            <>
                                <Typography sx={{ fontSize: 11, color: isDarkMode ? '#546e7a' : '#b0bec5' }}>·</Typography>
                                <PhoneIcon sx={{ fontSize: 11, color: isDarkMode ? '#78909c' : '#90a4ae' }} />
                                <Typography sx={{ fontSize: 11, color: isDarkMode ? '#b0bec5' : '#546e7a' }}>{ot.datosCliente_ot.clienteTelefono_ot}</Typography>
                            </>
                        )}
                        {ot.datosCliente_ot.clienteSegmento_ot && (
                            <Box sx={{ ml: 'auto', px: 0.7, py: 0.1, borderRadius: '6px', background: isDarkMode ? '#15294b' : '#e3f2fd', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: isDarkMode ? '#90caf9' : '#1565c0' }}>{ot.datosCliente_ot.clienteSegmento_ot}</Typography>
                            </Box>
                        )}
                    </Box>
                )}

                <Divider sx={{ my: 0.3, background: isDarkMode ? '#2d3a4a' : '#e3eaf2' }} />

                <Grid container columnSpacing={1} rowSpacing={0.3}>
                    <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                        <Row isDarkMode={isDarkMode} icon={<EventIcon sx={iconSx} />} label="Fecha" value={ot.strFechaProgramada || ot.fechaProgramada} />
                        <Row isDarkMode={isDarkMode} icon={<AccessTimeIcon sx={iconSx} />} label="Hora" value={ot.strHoraProgramada ? `${ot.strHoraProgramada}${ot.fechaProgramadaFin ? ' — ' + (ot.fechaProgramadaFin.split(' ')[1] || '') : ''}` : null} />
                        <Row isDarkMode={isDarkMode} icon={<TimerOutlinedIcon sx={iconSx} />} label="Duración" value={ot.tiempoEstimado ? `${ot.tiempoEstimado} min` : null} />
                        <Row isDarkMode={isDarkMode} icon={<LabelImportantIcon sx={iconSx} />} label="Clase" value={ot.claseOT} />
                        <Row isDarkMode={isDarkMode} icon={<BusinessIcon sx={iconSx} />} label="Segmento" value={ot.segmentoOT} />
                    </Grid>
                    <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                        <Row isDarkMode={isDarkMode} icon={<GroupsIcon sx={iconSx} />} label="Cuadrilla" value={ot.asignadoCuadrilla} />
                        <Row isDarkMode={isDarkMode} icon={<EngineeringIcon sx={iconSx} />} label="Empresa" value={ot.empresa} />
                        <Row isDarkMode={isDarkMode} icon={<LocationOnIcon sx={iconSx} />} label="Dir." value={ot.direccion_ot} />
                        {ot.ticketId_ot && (
                            <Row isDarkMode={isDarkMode} icon={<ConfirmationNumberIcon sx={iconSx} />} label="Ticket" value={`#${ot.ticketId_ot}`} />
                        )}
                        {ot.strTecnicos && (
                            <Row isDarkMode={isDarkMode} icon={<EngineeringIcon sx={iconSx} />} label="Técnicos" value={ot.strTecnicos} />
                        )}
                    </Grid>
                </Grid>

                {((ot.observaciones_ot && ot.observaciones_ot !== '-') || (ot.observacionesSigilosas_ot && ot.observacionesSigilosas_ot !== 'Sin observaciones' && ot.observacionesSigilosas_ot !== '-')) && (
                    <>
                        <Divider sx={{ my: 0.3, background: isDarkMode ? '#2d3a4a' : '#e3eaf2' }} />
                        {ot.observaciones_ot && ot.observaciones_ot !== '-' && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <NotesIcon sx={{ fontSize: 11, color: isDarkMode ? '#78909c' : '#90a4ae', mt: 0.1, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 11, color: isDarkMode ? '#b0bec5' : '#546e7a', lineHeight: 1.4 }}>{ot.observaciones_ot}</Typography>
                            </Box>
                        )}
                        {ot.observacionesSigilosas_ot && ot.observacionesSigilosas_ot !== 'Sin observaciones' && ot.observacionesSigilosas_ot !== '-' && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, background: isDarkMode ? '#2a2010' : '#fffde7', borderRadius: 1, px: 0.8, py: 0.4 }}>
                                <NotesIcon sx={{ fontSize: 11, color: isDarkMode ? '#ffd54f' : '#f9a825', mt: 0.1, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 11, color: isDarkMode ? '#ffd54f' : '#f57c00', lineHeight: 1.4 }}>{ot.observacionesSigilosas_ot}</Typography>
                            </Box>
                        )}
                    </>
                )}

                {ultimoHistorial && (
                    <>
                        <Divider sx={{ my: 0.3, background: isDarkMode ? '#2d3a4a' : '#e3eaf2' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <HistoryIcon sx={{ fontSize: 11, color: isDarkMode ? '#78909c' : '#90a4ae', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: isDarkMode ? '#78909c' : '#90a4ae' }}>Último estado:</Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: estadoColors.text }}>{ultimoHistorial.accionHistory_ot}</Typography>
                            <Typography sx={{ fontSize: 10, color: isDarkMode ? '#546e7a' : '#b0bec5', ml: 'auto', whiteSpace: 'nowrap' }}>
                                {ultimoHistorial.fechaIniHistory_ot ? FechaFunction.format(new Date(ultimoHistorial.fechaIniHistory_ot), 'dd/MM HH:mm') : ''}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>

            {/* FOOTER */}
            <Box sx={{
                px: 1.5, py: 0.8,
                background: isDarkMode ? '#111a26' : '#f8faff',
                borderTop: isDarkMode ? '1px solid #2d3a4a' : '1px solid #e3eaf2',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
                    <PersonIcon sx={{ fontSize: 11, color: isDarkMode ? '#546e7a' : '#b0bec5', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 10.5, color: isDarkMode ? '#78909c' : '#90a4ae', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ot.creadoPor} · {ot.creadoDate}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {ot.latitud_ot && ot.longitud_ot && (
                        <Box
                            component='a'
                            href={`https://www.google.com/maps?q=${ot.latitud_ot},${ot.longitud_ot}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            title='Ver en Google Maps'
                            sx={{
                                display: 'inline-flex', alignItems: 'center',
                                p: 0.5, borderRadius: '6px',
                                color: isDarkMode ? '#78909c' : '#90a4ae',
                                background: isDarkMode ? '#1e2d3d' : '#e3eaf2',
                                textDecoration: 'none',
                                '&:hover': { color: isDarkMode ? '#e0e7f0' : '#263238' }
                            }}
                        >
                            <MapIcon sx={{ fontSize: 13 }} />
                        </Box>
                    )}
                    <Box
                        component='a'
                        href={`https://ztask.45.224.140.25.nip.io/panel/detalle-ot/${ot.nroOTText_ot}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.4,
                            px: 1, py: 0.4, borderRadius: '6px',
                            fontSize: 11, fontWeight: 700,
                            textDecoration: 'none',
                            color: '#fff',
                            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                            boxShadow: '0 1px 4px rgba(21,101,192,0.3)',
                            transition: 'opacity 0.15s',
                            '&:hover': { opacity: 0.85 }
                        }}
                    >
                        Ver en ZTask <OpenInNewIcon sx={{ fontSize: 11 }} />
                    </Box>
                </Box>
            </Box>
        </Card>
    );
};

const CardOTZTask = ({ otsServicio }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const lista = Array.isArray(otsServicio) ? otsServicio : [];

    if (lista.length === 0) {
      return (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          p: 4, borderRadius: 3,
          background: isDarkMode
            ? 'linear-gradient(135deg, #1F2A40 0%, #1a2332 100%)'
            : 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)',
          border: isDarkMode ? '1px solid #2d3a4a' : '1px solid #dce6f5',
        }}>
          <Box sx={{
            width: 54, height: 54, borderRadius: '50%', mb: 2,
            background: isDarkMode
              ? 'linear-gradient(135deg, #1a2d4a 0%, #15294b 100%)'
              : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: isDarkMode
              ? '0 4px 14px rgba(0,0,0,0.35)'
              : '0 4px 14px rgba(21,101,192,0.15)',
          }}>
            <AssignmentIndIcon sx={{ fontSize: 28, color: isDarkMode ? '#64b5f6' : '#1565c0' }} />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDarkMode ? '#e0e7f0' : '#263238', mb: 0.5 }}>
            Sin órdenes de trabajo
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: isDarkMode ? '#78909c' : '#90a4ae', textAlign: 'center', maxWidth: 280, lineHeight: 1.6 }}>
            No se registraron OTs asociadas a este registro
          </Typography>
        </Box>
      );
    }

    return (
        <Grid container spacing={1.5}>
            {lista.map((ot) => (
                <Grid item xs={12} md={6} key={ot.id_ot}>
                    <OTCard ot={ot} isDarkMode={isDarkMode} />
                </Grid>
            ))}
        </Grid>
    );
};

export default CardOTZTask;
